import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { formsAPI, adminSystemAPI } from '../../utils/crmApi';
import FormShareModal from './FormShareModal';
import FormPreview from './FormPreview';
import { cn } from '../../lib/utils';
import toast from 'react-hot-toast';
import { BLOCKS, BLOCK_GROUPS, getBlocksByGroup } from './blockRegistry';
import { useDialog, Dialog, DialogHeader, DialogBody, DialogFooter } from '../kibo-ui/dialog';
import { PhoneMockup } from '../kibo-ui/phone-mockup';
import { useBranding } from '../../contexts/BrandingContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { ChartBarIcon } from '@heroicons/react/24/outline';

const defaultPage = { id: 'page-1', title: 'Page 1', elements: [] };

const FormBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { branding } = useBranding();
  const { hasFeature } = useSubscription();
  const isGlobalTemplate = searchParams.get('global') === 'true';
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: 'Untitled form', status: 'draft', pages: [defaultPage], settings: { progressBar: true } });
  const [activePageIdx, setActivePageIdx] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedElId, setSelectedElId] = useState(null);
  const [propTab, setPropTab] = useState('field'); // 'field' | 'validation' | 'logic' | 'mapping'
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' | 'mobile' (for preview modal only)
  const [previewModalOpen, setPreviewModalOpen] = useState(false); // Preview modal toggle
  const [cmdOpen, setCmdOpen] = useState(false);
  const [dragElId, setDragElId] = useState(null);
  const [dragPageIdx, setDragPageIdx] = useState(null);
  const [dirty, setDirty] = useState(false);
  const lastPagesRef = useRef(null);
  const lastSelectedElIdRef = useRef(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [blockSearch, setBlockSearch] = useState('');
  const searchRef = useRef(null);
  const [inviteEmailInput, setInviteEmailInput] = useState('');
  const { openDialog } = useDialog();
  
  // Determine if we're using agency branding (default for Starter, can be toggled for Pro+)
  const useAgencyBranding = form.theme?.useAgencyBranding !== false; // Default true unless explicitly disabled
  const canUseCustomBranding = hasFeature('customBranding'); // Pro+ feature

  const load = async () => {
    try {
      setLoading(true);
      
      if (isGlobalTemplate && id && id !== 'new') {
        // Load global template
        const response = await adminSystemAPI.getTemplates();
        const found = (response.data?.data || []).find(x => x.id === id || x._id === id);
        if (found) {
          setForm({
            name: found.title || 'Untitled form',
            description: found.description || '',
            category: found.category || '',
            tags: found.tags || [],
            pages: found.content?.pages || [defaultPage],
            settings: found.content?.settings || { progressBar: true },
            theme: found.content?.theme || {},
            logic: found.content?.logic || [],
            mapping: found.content?.mapping || [],
            status: found.status || 'draft'
          });
        }
      } else if (id && id !== 'new') {
        // Load regular agency form
        const { data } = await formsAPI.getAll();
        const found = (data.data || []).find(x => x._id === id);
        if (found) setForm(found);
      } else {
        // New form - use default
        setForm({ name: 'Untitled form', status: 'draft', pages: [defaultPage], settings: { progressBar: true } });
      }
    } catch (e) {
      console.error('Failed to load form', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id, isGlobalTemplate]);

  // Block search keyboard shortcut
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const beforeUnload = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);

  // Autosave every 10s when dirty (only for existing forms, not global templates)
  useEffect(() => {
    if (isGlobalTemplate || !id || id === 'new') return; // Skip autosave for global templates or new forms
    const interval = setInterval(async () => {
      if (!dirty || autoSaving) return;
      try {
        setAutoSaving(true);
        await formsAPI.update(id, { name: form.name, pages: form.pages, settings: form.settings, theme: form.theme, mapping: form.mapping });
        setDirty(false);
        setLastSavedAt(new Date());
      } catch (_) {
        // ignore for autosave; manual save is available
      } finally {
        setAutoSaving(false);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [dirty, autoSaving, id, form, isGlobalTemplate]);

  // Keyboard shortcuts for field navigation
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdOpen(true);
        return;
      }
      if (e.key === 'Escape') {
        setSelectedElId(null);
        return;
      }
      // Alt + Arrow reorders selected field
      if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        const els = form?.pages?.[activePageIdx]?.elements || [];
        if (!selectedElId || els.length === 0) return;
        const pages = [...form.pages];
        const elements = [...els];
        const idx = elements.findIndex(x => x.id === selectedElId);
        if (idx === -1) return;
        const dir = e.key === 'ArrowUp' ? -1 : 1;
        const newIdx = Math.max(0, Math.min(elements.length - 1, idx + dir));
        if (newIdx === idx) return;
        const [moved] = elements.splice(idx, 1);
        elements.splice(newIdx, 0, moved);
        pages[activePageIdx] = { ...pages[activePageIdx], elements };
        setForm({ ...form, pages });
        e.preventDefault();
        return;
      }
      if (!form?.pages?.[activePageIdx]?.elements?.length) return;
      const els = form.pages[activePageIdx].elements;
      const currentIdx = selectedElId ? els.findIndex(x => x.id === selectedElId) : -1;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = Math.min((currentIdx + 1 + els.length) % els.length, els.length - 1);
        setSelectedElId(els[next]?.id || null);
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = currentIdx <= 0 ? 0 : currentIdx - 1;
        setSelectedElId(els[prev]?.id || null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [form, activePageIdx, selectedElId]);

  const reorderElements = (fromId, toId) => {
    lastPagesRef.current = form.pages;
    const pages = [...form.pages];
    const elements = [...(pages[activePageIdx]?.elements || [])];
    const fromIdx = elements.findIndex(x => x.id === fromId);
    const toIdx = elements.findIndex(x => x.id === toId);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
    const [moved] = elements.splice(fromIdx, 1);
    elements.splice(toIdx, 0, moved);
    pages[activePageIdx] = { ...pages[activePageIdx], elements };
    setForm({ ...form, pages });
    setDirty(true);
  };

  const moveSelected = (dir) => {
    const pages = [...form.pages];
    const elements = [...(pages[activePageIdx]?.elements || [])];
    const idx = elements.findIndex(x => x.id === selectedElId);
    if (idx === -1) return;
    const newIdx = Math.max(0, Math.min(elements.length - 1, idx + dir));
    if (newIdx === idx) return;
    lastPagesRef.current = form.pages;
    const [moved] = elements.splice(idx, 1);
    elements.splice(newIdx, 0, moved);
    pages[activePageIdx] = { ...pages[activePageIdx], elements };
    setForm({ ...form, pages });
    setDirty(true);
    setSelectedElId(moved.id);
    toast((t) => (
      <div>
        Field moved. <button className="ml-2 underline" onClick={() => { setForm({ ...form, pages: lastPagesRef.current }); toast.dismiss(t.id); }}>Undo</button>
      </div>
    ));
  };

  const handleSave = async () => {
    // Validate form name
    if (!form.name || !form.name.trim()) {
      toast.error('Please enter a form name');
      return;
    }
    
    try {
      setSaving(true);
      
      if (isGlobalTemplate) {
        // Save as global template
        const globalTemplateData = {
          type: 'form',
          title: form.name.trim() || 'Untitled Form',
          description: form.description || '',
          category: form.category || '',
          tags: form.tags || [],
          content: {
            pages: form.pages || [],
            settings: form.settings || {},
            theme: form.theme || {},
            logic: form.logic || [],
            mapping: form.mapping || []
          }
        };
        
        // Only include slug when creating new template (backend will handle uniqueness)
        if (!id || id === 'new') {
          const baseSlug = form.name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'untitled-form';
          globalTemplateData.slug = `${baseSlug}-${Date.now()}`;
        }
        
        if (id && id !== 'new') {
          // Update existing template (don't send slug - it shouldn't change)
          await adminSystemAPI.updateTemplate(id, globalTemplateData);
          toast.success('Global template updated successfully');
        } else {
          const response = await adminSystemAPI.createTemplate(globalTemplateData);
          const newId = response.data?.data?._id;
          if (newId) {
            // Update URL to include the new ID
            navigate(`/forms/${newId}/builder?global=true`, { replace: true });
          }
          toast.success('Global template created successfully');
        }
      } else {
        // Save as regular agency form
        if (id && id !== 'new') {
          await formsAPI.update(id, { name: form.name.trim(), pages: form.pages, settings: form.settings, theme: form.theme, mapping: form.mapping });
        } else {
          const response = await formsAPI.create({ name: form.name.trim(), pages: form.pages, settings: form.settings, theme: form.theme, mapping: form.mapping });
          const newId = response.data?.data?._id;
          if (newId) {
            navigate(`/forms/${newId}/builder`, { replace: true });
          }
        }
        toast.success('Form saved successfully');
      }
      
      setDirty(false);
    } catch (e) {
      console.error('Failed to save form', e);
      const errorMessage = e.response?.data?.message || 'Failed to save form. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      setSaving(true);
      const { data } = await formsAPI.publish(id);
      setForm(data.data);
    } catch (e) {
      console.error('Failed to publish', e);
    } finally {
      setSaving(false);
    }
  };

  const addBlock = (blockType) => {
    const blockDef = BLOCKS[blockType];
    if (!blockDef) return;
    
    // Create element from block defaults
    const defaults = { ...blockDef.defaults };
    const newField = {
      id: `el-${Date.now()}`,
      type: blockDef.type,
      key: defaults.key || `${blockType}_${Date.now()}`,
      label: typeof defaults.label === 'string' ? { en: defaults.label } : (defaults.label || { en: blockDef.label }),
      ...defaults,
    };
    
    const pages = [...(form.pages || [])];
    if (pages.length === 0) pages.push({ ...defaultPage });
    const idx = Math.min(activePageIdx, pages.length - 1);
    pages[idx] = { ...pages[idx], elements: [...(pages[idx].elements || []), newField] };
    setForm({ ...form, pages });
    setDirty(true);
  };

  const addPage = () => {
    const nextNum = (form.pages?.length || 0) + 1;
    const newPage = { id: `page-${Date.now()}`, title: `Page ${nextNum}`, elements: [] };
    const pages = [...(form.pages || []), newPage];
    setForm({ ...form, pages });
    setActivePageIdx(pages.length - 1);
    setDirty(true);
  };

  const deletePage = (pageIdx) => {
    if ((form.pages || []).length <= 1) {
      openDialog({
        variant: 'warning',
        title: 'Cannot delete page',
        description: 'A form must have at least one page.',
        confirmText: 'OK',
        onConfirm: () => {},
        onClose: () => {}
      });
      return;
    }
    openDialog({
      variant: 'danger',
      title: 'Delete page',
      description: `Are you sure you want to delete "${form.pages[pageIdx]?.title || `Page ${pageIdx + 1}`}"? All fields on this page will be lost.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => {
        lastPagesRef.current = form.pages;
        const pages = [...(form.pages || [])];
        pages.splice(pageIdx, 1);
        setForm({ ...form, pages });
        if (activePageIdx >= pages.length) {
          setActivePageIdx(Math.max(0, pages.length - 1));
        }
        setDirty(true);
        toast((t) => (
          <div>
            Page deleted. <button className="ml-2 underline" onClick={() => { setForm({ ...form, pages: lastPagesRef.current }); toast.dismiss(t.id); }}>Undo</button>
          </div>
        ));
      },
      onClose: () => {}
    });
  };

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const handleInvite = () => {
    setInviteEmailInput('');
    setInviteDialogOpen(true);
  };

  const handleInviteSubmit = async () => {
    const emails = inviteEmailInput.split(',').map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) {
      setInviteDialogOpen(false);
      openDialog({
        variant: 'warning',
        title: 'Invalid input',
        description: 'Please enter at least one valid email address.',
        confirmText: 'OK',
        onConfirm: () => {},
        onClose: () => {}
      });
      return;
    }
    try {
      await formsAPI.invite(id, emails);
      setInviteDialogOpen(false);
      openDialog({
        variant: 'success',
        title: 'Invites sent',
        description: `Successfully sent ${emails.length} invite(s).`,
        confirmText: 'OK',
        onConfirm: () => {},
        onClose: () => {}
      });
    } catch (e) {
      console.error('Invite failed', e);
      setInviteDialogOpen(false);
      openDialog({
        variant: 'error',
        title: 'Failed to send invites',
        description: e.response?.data?.message || 'An error occurred while sending invites.',
        confirmText: 'OK',
        onConfirm: () => {},
        onClose: () => {}
      });
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-0 space-y-4">
      {/* Builder AppBar - Redesigned */}
      <div className="sticky top-14 z-30 bg-background border-b">
        <div className="px-6 py-3">
          {/* Top Row: Title, Status, Unsaved indicator */}
          <div className="flex items-center gap-3 mb-3">
            <div className="relative group">
              <input
                className="text-xl font-semibold bg-transparent border border-transparent hover:border-input/50 focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 rounded px-2 -ml-2 tracking-tight transition-all cursor-text min-w-[200px]"
                value={form.name || ''}
                onChange={e => { setForm({ ...form, name: e.target.value }); setDirty(true); }}
                aria-label="Form title"
                placeholder="Click to edit form name"
                title="Click to edit form name"
              />
              <div className="absolute -bottom-5 left-0 text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                Click to edit
              </div>
            </div>
            <span className={cn(
              "text-[11px] font-medium px-2.5 py-1 rounded-full border",
              form.status === 'published' ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-300"
            )}>
              {form.status === 'published' ? 'Published' : 'Draft'}
            </span>
            {dirty && (
              <span className="text-xs text-amber-600 font-medium flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                Unsaved changes
              </span>
            )}
            {!dirty && lastSavedAt && (
              <span className="text-xs text-muted-foreground">
                Saved {lastSavedAt.toLocaleTimeString()}
              </span>
            )}
            {autoSaving && (
              <span className="text-xs" style={{ color: branding?.primaryColor || '#3b82f6' }}>Saving…</span>
            )}
          </div>
          
          {/* Bottom Row: Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setSelectedElId(null)} 
                className={cn(
                  "px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors",
                  !selectedElId ? "bg-primary/10 border-primary/30 text-primary" : "hover:bg-muted border-border"
                )}
              >
                Form settings
              </button>
              <button 
                onClick={() => setPreviewModalOpen(true)} 
                className="px-3 py-1.5 rounded-lg border border-border hover:bg-muted text-sm font-medium transition-colors"
              >
                Preview
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => {
                  if (dirty) {
                    openDialog({
                      variant: 'warning',
                      title: 'Discard unsaved changes?',
                      description: 'You have unsaved changes. Are you sure you want to leave without saving?',
                      confirmText: 'Discard',
                      cancelText: 'Cancel',
                      onConfirm: () => navigate('/forms'),
                      onClose: () => {}
                    });
                  } else {
                    navigate('/forms');
                  }
                }} 
                className="px-3 py-1.5 rounded-lg border border-border hover:bg-muted text-sm font-medium transition-colors"
              >
                Close
              </button>
              <button 
                onClick={handleSave} 
                disabled={saving || !dirty} 
                className={cn(
                  "px-4 py-1.5 rounded-lg border text-sm font-medium transition-colors",
                  dirty ? "hover:opacity-80" : "border-border text-muted-foreground cursor-not-allowed"
                )}
                style={dirty ? {
                  borderColor: branding?.primaryColor || '#3b82f6',
                  backgroundColor: `${branding?.primaryColor || '#3b82f6'}15`,
                  color: branding?.primaryColor || '#1e40af'
                } : {}}
              >
                Save
              </button>
              <button 
                onClick={handlePublish} 
                disabled={saving || form.status === 'published'} 
                className={cn(
                  "px-4 py-1.5 rounded-lg text-sm font-medium transition-colors",
                  form.status === 'published' 
                    ? "bg-muted text-muted-foreground cursor-not-allowed" 
                    : "bg-primary text-white hover:opacity-90"
                )}
              >
                {form.status === 'published' ? 'Published' : 'Publish'}
              </button>
              <button 
                onClick={handleInvite} 
                disabled={saving || form.status !== 'published'} 
                className={cn(
                  "px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors",
                  form.status !== 'published' 
                    ? "border-border text-muted-foreground cursor-not-allowed" 
                    : "border-border hover:bg-muted"
                )}
                title={form.status !== 'published' ? 'Publish form first to send invites' : ''}
              >
                Invite
              </button>
              <button 
                onClick={() => setShareOpen(true)} 
                disabled={saving || form.status !== 'published'} 
                className={cn(
                  "px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors",
                  form.status !== 'published' 
                    ? "border-border text-muted-foreground cursor-not-allowed" 
                    : "border-border hover:bg-muted"
                )}
                title={form.status !== 'published' ? 'Publish form first to share' : ''}
              >
                Share
              </button>
              {form.status === 'published' && (
                <button 
                  onClick={() => navigate(`/forms/${id}/analytics`)} 
                  className="px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors flex items-center gap-1.5 hover:opacity-80"
                  style={{
                    borderColor: `${branding?.primaryColor || '#3b82f6'}40`,
                    color: branding?.primaryColor || '#1e40af'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${branding?.primaryColor || '#3b82f6'}15`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  title="View form analytics"
                >
                  <ChartBarIcon className="w-4 h-4" />
                  Analytics
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Builder Content */}
      <div className="px-6 py-4 grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-3">
          <div className="rounded-2xl border bg-card shadow-sm">
            <div className="p-3 border-b">
              <div className="text-sm font-semibold text-slate-800">Blocks</div>
              <input 
                ref={searchRef}
                placeholder="Search blocks… ( / )" 
                className="mt-2 w-full text-sm px-2 py-1.5 rounded-lg border bg-background focus:outline-2 focus:outline-primary" 
                value={blockSearch}
                onChange={(e) => setBlockSearch(e.target.value)}
              />
            </div>
            <div className="p-3 space-y-4 max-h-[calc(100vh-250px)] overflow-y-auto scrollbar-thin">
              {(() => {
                const filteredBlocks = blockSearch
                  ? Object.values(BLOCKS).filter(b => 
                      b.label.toLowerCase().includes(blockSearch.toLowerCase()) ||
                      b.description?.toLowerCase().includes(blockSearch.toLowerCase()) ||
                      b.type.toLowerCase().includes(blockSearch.toLowerCase())
                    )
                  : null;
                
                if (blockSearch && filteredBlocks.length === 0) {
                  return (
                    <div className="text-center py-8 text-sm text-muted-foreground">
                      <div className="mb-2">No blocks found</div>
                      <button onClick={() => setBlockSearch('')} className="text-xs underline">Clear search</button>
                    </div>
                  );
                }
                
                const groupsToShow = blockSearch 
                  ? [{ group: 'Results', blocks: filteredBlocks }]
                  : BLOCK_GROUPS.map(group => ({ group, blocks: getBlocksByGroup(group) }));
                
                return groupsToShow.map(({ group, blocks }) => {
                  if (blocks.length === 0) return null;
                  return (
                    <div key={group}>
                      <div className="text-xs font-medium mb-2 text-muted-foreground uppercase tracking-wide sticky top-0 bg-card py-1">{group}</div>
                      <div className="space-y-2">
                        {blocks.map(block => (
                          <BlockCard
                            key={block.type}
                            label={block.label}
                            description={block.description}
                            icon={block.icon}
                            tier={block.tier}
                            onClick={() => { addBlock(block.type); setBlockSearch(''); }}
                          />
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
              <div className="pt-2 border-t">
                <button className="w-full text-left px-3 py-2 rounded-lg border hover:bg-muted" onClick={addPage}>+ Add Page</button>
              </div>
              <div className="pt-3 border-t">
                <div className="text-xs font-medium mb-2 text-muted-foreground">Starter templates</div>
                <TemplateButton label="Lead Intake" onClick={() => {
                  const pages = [{ id: `page-${Date.now()}`, title: 'Lead Intake', elements: [
                    { id: `el-${Date.now()}-n`, key: 'full_name', type: 'full_name', label: { en: 'Full name' }, required: true },
                    { id: `el-${Date.now()}-e`, key: 'email', type: 'email', label: { en: 'Email' }, required: true },
                    { id: `el-${Date.now()}-t`, key: 'notes', type: 'long_text', label: { en: 'Notes' } },
                  ] }];
                  setForm({ ...form, pages }); setDirty(true);
                }} />
                <TemplateButton label="Simple Survey" onClick={() => {
                  const pages = [{ id: `page-${Date.now()}`, title: 'Simple Survey', elements: [
                    { id: `el-${Date.now()}-d`, key: 'intro', type: 'description', label: { en: 'We value your feedback' }, helpText: { en: 'This survey takes under 1 minute.' } },
                    { id: `el-${Date.now()}-e`, key: 'email', type: 'email', label: { en: 'Email (optional)' } },
                    { id: `el-${Date.now()}-c`, key: 'comments', type: 'long_text', label: { en: 'Comments' } },
                  ] }];
                  setForm({ ...form, pages }); setDirty(true);
                }} />
              </div>
            </div>
          </div>
          <div className="mt-4 p-2 bg-muted rounded text-xs text-muted-foreground">Tip: Click a field to edit its properties.</div>
        </div>
        <div className="col-span-12 lg:col-span-6">
          <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
            {/* Page tabs */}
            <div className="p-3 border-b flex items-center justify-between sticky top-0 bg-card z-10">
              <div className="flex gap-2 overflow-x-auto flex-1">
                {(form.pages || []).map((p, i) => (
                  <div key={p.id} className="flex items-center group">
                    <button
                      key={p.id}
                      draggable
                      onDragStart={() => setDragPageIdx(i)}
                      onDragOver={(e) => { 
                        e.preventDefault(); 
                        e.currentTarget.classList.add('ring-2', 'ring-primary/40'); 
                      }}
                      onDragLeave={(e) => { 
                        e.currentTarget.classList.remove('ring-2', 'ring-primary/40'); 
                      }}
                      onDrop={(e) => {
                        e.currentTarget.classList.remove('ring-2', 'ring-primary/40');
                        e.preventDefault();
                        if (dragPageIdx === null || dragPageIdx === i) return;
                        lastPagesRef.current = form.pages;
                        const pages = [...(form.pages || [])];
                        const [moved] = pages.splice(dragPageIdx, 1);
                        pages.splice(i, 0, moved);
                        setForm({ ...form, pages });
                        setActivePageIdx(i);
                        setDragPageIdx(null);
                        setDirty(true);
                        toast((t) => (
                          <div>
                            Page reordered. <button className="ml-2 underline" onClick={() => { setForm({ ...form, pages: lastPagesRef.current }); toast.dismiss(t.id); }}>Undo</button>
                          </div>
                        ));
                      }}
                      onClick={() => setActivePageIdx(i)}
                      className={cn("px-3 py-1.5 rounded-full border text-sm transition-all", i===activePageIdx ? "bg-primary/10 border-primary/30 text-primary" : "bg-muted hover:border-primary/20")}
                    >
                      <span className="mr-1.5 opacity-60">📄</span>
                      {p.title || `Page ${i+1}`}
                    </button>
                    {(form.pages || []).length > 1 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); deletePage(i); }}
                        className="ml-1 opacity-0 group-hover:opacity-60 hover:opacity-100 text-red-600 hover:text-red-700 transition-opacity"
                        title="Delete page"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  onClick={addPage}
                  className="px-3 py-1.5 rounded-full border bg-muted hover:bg-muted/80 text-sm whitespace-nowrap"
                >
                  + New page
                </button>
              </div>
              {form.settings?.progressBar && (
                <div className="text-xs text-muted-foreground ml-2">Page {activePageIdx+1} of {form.pages?.length || 1}</div>
              )}
            </div>

            {/* Canvas */}
            <div className="p-6 bg-[linear-gradient(0deg,transparent_24px,rgba(0,0,0,0.03)_25px),linear-gradient(90deg,transparent_24px,rgba(0,0,0,0.03)_25px)] bg-[size:25px_25px] max-w-[860px] mx-auto" onClick={() => setSelectedElId(null)}>
              {((form.pages?.[activePageIdx]?.elements) || []).length === 0 ? (
                <div className="border border-dashed rounded-xl p-10 text-center text-sm text-muted-foreground">
                  Drag or click a block to add your first field.
                </div>
              ) : (
                <div className="space-y-3">
                  {form.pages[activePageIdx].elements.map(el => (
                    <div
                      key={el.id}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setSelectedElId(el.id); }}
                      draggable
                      onDragStart={() => setDragElId(el.id)}
                      onDragOver={(e) => { 
                        e.preventDefault(); 
                        e.currentTarget.dataset.drop = 'true';
                      }}
                      onDragLeave={(e) => { 
                        delete e.currentTarget.dataset.drop;
                      }}
                      onDrop={(e) => { 
                        delete e.currentTarget.dataset.drop;
                        e.preventDefault(); 
                        if (dragElId) reorderElements(dragElId, el.id); 
                        setDragElId(null); 
                      }}
                      className={cn(
                        "group relative border rounded-xl p-3 bg-background shadow-sm cursor-move transition-all",
                        selectedElId===el.id ? "ring-2 ring-primary/50 border-primary/30" : "hover:border-muted-foreground/30",
                        "[&[data-drop]]:outline [&[data-drop]]:outline-2 [&[data-drop]]:outline-primary/60 [&[data-drop]]:-outline-offset-2"
                      )}
                    >
                      {/* Drag handle */}
                      <div className="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-60 transition cursor-grab active:cursor-grabbing">
                        <div className="h-8 w-3 grid grid-rows-3 gap-[3px]">
                          <span className="h-[3px] rounded bg-slate-300/80"></span>
                          <span className="h-[3px] rounded bg-slate-300/80"></span>
                          <span className="h-[3px] rounded bg-slate-300/80"></span>
                        </div>
                      </div>
                      
                      {/* Quickbar */}
                      {selectedElId === el.id && (
                        <div className="absolute -top-3 right-3 flex gap-1 z-10">
                          <QuickChip onClick={(e) => { e.stopPropagation();
                            const pages = [...form.pages];
                            const elements = [...pages[activePageIdx].elements];
                            const idx = elements.findIndex(x => x.id === el.id);
                            if (idx > 0) {
                              [elements[idx], elements[idx-1]] = [elements[idx-1], elements[idx]];
                              pages[activePageIdx] = { ...pages[activePageIdx], elements };
                              setForm({ ...form, pages });
                              setDirty(true);
                            }
                          }}>↑</QuickChip>
                          <QuickChip onClick={(e) => { e.stopPropagation();
                            const pages = [...form.pages];
                            const elements = [...pages[activePageIdx].elements];
                            const idx = elements.findIndex(x => x.id === el.id);
                            if (idx < elements.length - 1) {
                              [elements[idx], elements[idx+1]] = [elements[idx+1], elements[idx]];
                              pages[activePageIdx] = { ...pages[activePageIdx], elements };
                              setForm({ ...form, pages });
                              setDirty(true);
                            }
                          }}>↓</QuickChip>
                          <QuickChip onClick={(e) => { e.stopPropagation();
                            const pages = [...form.pages];
                            const elements = [...pages[activePageIdx].elements];
                            const idx = elements.findIndex(x => x.id === el.id);
                            const copy = { ...elements[idx], id: `el-${Date.now()}` };
                            elements.splice(idx+1, 0, copy);
                            pages[activePageIdx] = { ...pages[activePageIdx], elements };
                            setForm({ ...form, pages });
                            setDirty(true);
                          }}>Duplicate</QuickChip>
                          <QuickChip onClick={(e) => { e.stopPropagation();
                            lastPagesRef.current = form.pages;
                            lastSelectedElIdRef.current = selectedElId;
                            const pages = [...form.pages];
                            const elements = pages[activePageIdx].elements.filter(x => x.id !== el.id);
                            pages[activePageIdx] = { ...pages[activePageIdx], elements };
                            setForm({ ...form, pages });
                            setDirty(true);
                            if (selectedElId === el.id) setSelectedElId(null);
                            toast((t) => (
                              <div className="flex items-center justify-between gap-3 py-1">
                                <span className="text-sm text-foreground">Field deleted.</span>
                                <button 
                                  className="font-medium hover:underline px-2 py-1 rounded transition-colors"
                                  style={{ color: branding?.primaryColor || '#3b82f6' }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = `${branding?.primaryColor || '#3b82f6'}15`;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }} 
                                  onClick={() => { 
                                    setForm({ ...form, pages: lastPagesRef.current }); 
                                    setSelectedElId(lastSelectedElIdRef.current);
                                    toast.dismiss(t.id);
                                  }}
                                >
                                  Undo
                                </button>
                              </div>
                            ), { 
                              duration: 6000,
                              id: `field-delete-${el.id}`,
                              position: 'bottom-right',
                            });
                          }} className="hover:bg-rose-50">Delete</QuickChip>
                        </div>
                      )}
                      
                      {(() => {
                        const hasIssues = (el.required && !el.label?.en) || !el.key || el.key.length < 2;
                        const elLabel = typeof el.label === 'object' ? (el.label?.en || 'Untitled question') : (el.label || 'Untitled question');
                        return (
                          <div className="flex items-start gap-2 pl-6">
                            <span className="text-sm opacity-60 mt-0.5">📝</span>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="text-sm font-medium text-slate-800">{elLabel}</div>
                                {hasIssues && (
                                  <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">Needs attention</span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-600 mb-2">Key: {el.key}</div>
                              {/* Inline editor */}
                              <input
                                className="w-full text-sm bg-background border rounded-lg px-3 py-2 focus:outline-2 focus:outline-primary"
                                value={elLabel}
                                onChange={e => {
                                  const pages = [...form.pages];
                                  const elements = [...pages[activePageIdx].elements];
                                  const idx = elements.findIndex(x => x.id === el.id);
                                  elements[idx] = { ...elements[idx], label: { ...(elements[idx].label || {}), en: e.target.value } };
                                  pages[activePageIdx] = { ...pages[activePageIdx], elements };
                                  setForm({ ...form, pages });
                                  setDirty(true);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              {/* Validation preview */}
                              <div className="mt-1 text-[11px]">
                                {el.required && <span className="text-rose-600">Required</span>}
                                {el.validations?.regex && (
                                  <span className="ml-2 text-slate-500">Regex: /{el.validations.regex}/</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-3">
          <div className="rounded-2xl border bg-card shadow-sm flex flex-col max-h-[calc(100vh-180px)]">
            <div className="p-3 border-b flex items-center justify-between sticky top-0 bg-card z-10">
              <div className="text-sm font-semibold text-slate-800">{selectedElId ? 'Field properties' : 'Form properties'}</div>
              {selectedElId && (() => {
                const el = form.pages?.[activePageIdx]?.elements?.find(x => x.id === selectedElId);
                return el ? (
                  <div className="text-[11px] px-2 py-0.5 rounded-full border bg-muted capitalize">{el.type}</div>
                ) : null;
              })()}
            </div>
            <div className="p-3 flex-1 overflow-y-auto scrollbar-thin">
          {!selectedElId && (
            <div className="space-y-3">
              <div className="flex gap-2 text-xs">
                <span className="px-2 py-1 rounded-full border bg-muted">Form</span>
                <span className="px-2 py-1 rounded-full border">Design</span>
                <span className="px-2 py-1 rounded-full border">Localization</span>
              </div>
              
              {/* Branding Source Toggle */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-xs font-medium text-foreground">Branding Source</div>
                    <div className="text-[11px] text-muted-foreground">Use agency or custom</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!useAgencyBranding && canUseCustomBranding}
                      disabled={!canUseCustomBranding}
                      onChange={(e) => {
                        const newUseAgency = !e.target.checked;
                        const theme = { ...(form.theme || {}) };
                        if (newUseAgency) {
                          // Switch to agency branding - apply agency colors/logo
                          theme.useAgencyBranding = true;
                          theme.primary = branding.primaryColor || '#3B82F6';
                          theme.logoUrl = branding.agencyLogo || null;
                          theme.coverImageUrl = branding.agencyCoverImage || null;
                          delete theme.customPrimary;
                          delete theme.customLogoUrl;
                          delete theme.customCoverImageUrl;
                        } else {
                          // Switch to custom - preserve current values as custom
                          theme.useAgencyBranding = false;
                          theme.customPrimary = theme.primary || branding.primaryColor || '#3B82F6';
                          theme.customLogoUrl = theme.logoUrl;
                          theme.customCoverImageUrl = theme.coverImageUrl;
                        }
                        setForm({ ...form, theme });
                        setDirty(true);
                      }}
                      className="sr-only peer"
                    />
                    <div 
                      className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-input after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                      style={{ 
                        '--tw-ring-color': branding?.primaryColor || '#3b82f6'
                      }}
                      ref={(el) => {
                        if (el) {
                          const checked = el.previousElementSibling?.checked;
                          el.style.backgroundColor = checked ? (branding?.primaryColor || '#3b82f6') : '#e5e7eb';
                          if (el.previousElementSibling) {
                            el.previousElementSibling.addEventListener('change', (e) => {
                              el.style.backgroundColor = e.target.checked ? (branding?.primaryColor || '#3b82f6') : '#e5e7eb';
                            });
                          }
                        }
                      }}
                    ></div>
                  </label>
                </div>
                {!canUseCustomBranding && (
                  <div className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                    ⚠️ Custom branding requires Pro plan or higher
                  </div>
                )}
                {useAgencyBranding && (
                  <div 
                    className="text-[11px] rounded px-2 py-1 mt-1 border"
                    style={{
                      color: branding?.primaryColor || '#3b82f6',
                      backgroundColor: `${branding?.primaryColor || '#3b82f6'}15`,
                      borderColor: `${branding?.primaryColor || '#3b82f6'}40`
                    }}
                  >
                    ✓ Using agency branding: {branding.agencyName || 'Your agency'}
                  </div>
                )}
              </div>
              
              {/* Primary Color */}
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Primary color
                  {useAgencyBranding && <span className="ml-1" style={{ color: branding?.primaryColor || '#3b82f6' }}>(from agency)</span>}
                </div>
                <input
                  type="color"
                  className="h-8 w-10 rounded border"
                  value={useAgencyBranding ? (branding.primaryColor || form.theme?.primary || '#3B82F6') : (form.theme?.customPrimary || form.theme?.primary || '#3B82F6')}
                  disabled={useAgencyBranding}
                  onChange={(e) => {
                    const theme = { ...(form.theme || {}), customPrimary: e.target.value };
                    if (!useAgencyBranding) theme.primary = e.target.value;
                    setForm({ ...form, theme });
                    setDirty(true);
                  }}
                />
                {useAgencyBranding && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Agency: {branding.primaryColor || '#3B82F6'}
                  </div>
                )}
              </div>
              
              {/* Logo */}
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Logo
                  {useAgencyBranding && <span className="ml-1" style={{ color: branding?.primaryColor || '#3b82f6' }}>(from agency)</span>}
                </div>
                {useAgencyBranding ? (
                  <div className="text-[11px] text-muted-foreground mb-2">
                    {branding.agencyLogo ? (
                      <div className="flex items-center gap-2">
                        <img src={branding.agencyLogo} alt="Agency logo" className="h-8 object-contain" />
                        <span>Agency logo active</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">No agency logo set</span>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    className="w-full text-xs px-2 py-1.5 rounded border"
                    placeholder="Logo URL"
                    value={form.theme?.customLogoUrl || form.theme?.logoUrl || ''}
                    onChange={(e) => {
                      const theme = { ...(form.theme || {}), customLogoUrl: e.target.value };
                      if (!useAgencyBranding) theme.logoUrl = e.target.value;
                      setForm({ ...form, theme });
                      setDirty(true);
                    }}
                  />
                )}
              </div>
              
              {/* Cover Image */}
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Cover image
                  {useAgencyBranding && <span className="ml-1" style={{ color: branding?.primaryColor || '#3b82f6' }}>(from agency)</span>}
                </div>
                {useAgencyBranding ? (
                  <div className="text-[11px] text-muted-foreground mb-2">
                    {branding.agencyCoverImage ? (
                      <div className="flex items-center gap-2">
                        <img src={branding.agencyCoverImage} alt="Cover" className="h-12 w-20 object-cover rounded" />
                        <span>Agency cover active</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">No cover image set</span>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    className="w-full text-xs px-2 py-1.5 rounded border"
                    placeholder="Cover image URL"
                    value={form.theme?.customCoverImageUrl || form.theme?.coverImageUrl || ''}
                    onChange={(e) => {
                      const theme = { ...(form.theme || {}), customCoverImageUrl: e.target.value };
                      if (!useAgencyBranding) theme.coverImageUrl = e.target.value;
                      setForm({ ...form, theme });
                      setDirty(true);
                    }}
                  />
                )}
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Font scale</div>
                <select className="w-full text-sm px-2 py-1.5 rounded border" value={form.theme?.fontScale || 'md'} onChange={(e) => {
                  const theme = { ...(form.theme || {}), fontScale: e.target.value };
                  setForm({ ...form, theme }); setDirty(true);
                }}>
                  <option value="sm">Small</option>
                  <option value="md">Medium</option>
                  <option value="lg">Large</option>
                </select>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Corner radius</div>
                <input type="number" className="w-full text-sm px-2 py-1.5 rounded border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring" value={form.theme?.radius ?? 12} onChange={(e) => {
                  const theme = { ...(form.theme || {}), radius: Number(e.target.value) };
                  setForm({ ...form, theme }); setDirty(true);
                }} />
              </div>
              <div className="border-t pt-3">
                <div className="text-xs text-muted-foreground mb-1">Languages</div>
                <input className="w-full text-sm px-2 py-1.5 rounded border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring" placeholder="Default (e.g., en, es-ES)" value={form.localeDefault || ''} onChange={(e) => { setForm({ ...form, localeDefault: e.target.value }); setDirty(true); }} />
                <input className="mt-2 w-full text-sm px-2 py-1.5 rounded border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring" placeholder="Enabled (comma separated)" value={(form.locales || []).join(', ')} onChange={(e) => { setForm({ ...form, locales: e.target.value.split(',').map(s=>s.trim()).filter(Boolean) }); setDirty(true); }} />
              </div>
            </div>
          )}
          {selectedElId && (() => {
            const el = form.pages?.[activePageIdx]?.elements?.find(x => x.id === selectedElId);
            if (!el) return <div className="text-xs text-muted-foreground">Field not found.</div>;
            return (
              <div className="space-y-3">
                {/* Segmented tabs */}
                <div className="inline-flex rounded-lg border overflow-hidden text-xs mb-3">
                  {['field','validation','logic','mapping'].map(t => (
                    <button key={t} onClick={() => setPropTab(t)}
                      className={cn("px-3 py-1.5", propTab===t && "bg-muted font-medium")}>
                      {t[0].toUpperCase()+t.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <button className="px-2 py-1 border rounded" onClick={() => moveSelected(-1)}>Move up</button>
                  <button className="px-2 py-1 border rounded" onClick={() => moveSelected(1)}>Move down</button>
                  <button className="ml-auto px-2 py-1 border rounded" onClick={() => {
                    const pages = [...form.pages];
                    const elements = [...pages[activePageIdx].elements];
                    const idx = elements.findIndex(x => x.id === selectedElId);
                    if (idx === -1) return;
                    const copy = { ...elements[idx], id: `el-${Date.now()}` };
                    elements.splice(idx+1, 0, copy);
                    pages[activePageIdx] = { ...pages[activePageIdx], elements };
                    setForm({ ...form, pages });
                    setDirty(true);
                  }}>Duplicate</button>
                  <button className="px-2 py-1 border rounded hover:bg-red-50" onClick={() => {
                    lastPagesRef.current = form.pages;
                    lastSelectedElIdRef.current = selectedElId;
                    const pages = [...form.pages];
                    const elements = pages[activePageIdx].elements.filter(x => x.id !== selectedElId);
                    pages[activePageIdx] = { ...pages[activePageIdx], elements };
                    setForm({ ...form, pages });
                    setDirty(true);
                    setSelectedElId(null);
                    toast((t) => (
                      <div className="flex items-center justify-between gap-3 py-1">
                        <span className="text-sm text-foreground">Field deleted.</span>
                        <button 
                          className="font-medium text-blue-600 hover:text-blue-700 hover:underline px-2 py-1 rounded hover:bg-blue-50 transition-colors" 
                          onClick={() => { 
                            setForm({ ...form, pages: lastPagesRef.current }); 
                            setSelectedElId(lastSelectedElIdRef.current);
                            toast.dismiss(t.id);
                          }}
                        >
                          Undo
                        </button>
                      </div>
                    ), { 
                      duration: 6000,
                      id: `field-delete-${selectedElId}`,
                      position: 'bottom-right',
                    });
                  }}>Delete</button>
                </div>
                {propTab === 'field' && (
                <>
                <div>
                  <label className="block text-xs text-foreground mb-1">Label</label>
                  <input className="w-full px-2 py-1.5 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring" value={el.label?.en || ''} onChange={e => {
                    const pages2 = [...form.pages];
                    const elements = [...pages2[activePageIdx].elements];
                    const idxEl = elements.findIndex(x => x.id === el.id);
                    elements[idxEl] = { ...elements[idxEl], label: { ...(elements[idxEl].label || {}), en: e.target.value } };
                    pages2[activePageIdx] = { ...pages2[activePageIdx], elements };
                    setForm({ ...form, pages: pages2 });
                    setDirty(true);
                  }} />
                </div>
                <div>
                  <label className="block text-xs text-foreground mb-1">Placeholder</label>
                  <input className="w-full px-2 py-1.5 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring" value={el.placeholder?.en || ''} onChange={e => {
                    const pages2 = [...form.pages];
                    const elements = [...pages2[activePageIdx].elements];
                    const idxEl = elements.findIndex(x => x.id === el.id);
                    elements[idxEl] = { ...elements[idxEl], placeholder: { ...(elements[idxEl].placeholder || {}), en: e.target.value } };
                    pages2[activePageIdx] = { ...pages2[activePageIdx], elements };
                    setForm({ ...form, pages: pages2 });
                    setDirty(true);
                  }} />
                </div>
                <div>
                  <label className="block text-xs text-slate-700 mb-1">Help text</label>
                  <textarea rows={2} className="w-full px-2 py-1.5 border rounded-lg focus:outline-2 focus:outline-primary" value={el.helpText?.en || ''} onChange={e => {
                    const pages2 = [...form.pages];
                    const elements = [...pages2[activePageIdx].elements];
                    const idxEl = elements.findIndex(x => x.id === el.id);
                    elements[idxEl] = { ...elements[idxEl], helpText: { ...(elements[idxEl].helpText || {}), en: e.target.value } };
                    pages2[activePageIdx] = { ...pages2[activePageIdx], elements };
                    setForm({ ...form, pages: pages2 });
                    setDirty(true);
                  }} />
                </div>
                <div className="flex items-center gap-2">
                  <input id="req" type="checkbox" checked={!!el.required} onChange={e => {
                    const pages2 = [...form.pages];
                    const elements = [...pages2[activePageIdx].elements];
                    const idxEl = elements.findIndex(x => x.id === el.id);
                    elements[idxEl] = { ...elements[idxEl], required: e.target.checked };
                    pages2[activePageIdx] = { ...pages2[activePageIdx], elements };
                    setForm({ ...form, pages: pages2 });
                    setDirty(true);
                  }} />
                  <label htmlFor="req" className="text-xs text-foreground">Required</label>
                </div>
                
                {/* Options editor for choice fields */}
                {(el.type === 'single_choice' || el.type === 'multi_select') && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-slate-700">Options</label>
                      <button
                        onClick={() => {
                          const pages2 = [...form.pages];
                          const elements = [...pages2[activePageIdx].elements];
                          const idxEl = elements.findIndex(x => x.id === el.id);
                          const currentOptions = elements[idxEl].options || [];
                          const newOption = {
                            value: `option_${Date.now()}`,
                            label: { en: 'New option' }
                          };
                          elements[idxEl] = { ...elements[idxEl], options: [...currentOptions, newOption] };
                          pages2[activePageIdx] = { ...pages2[activePageIdx], elements };
                          setForm({ ...form, pages: pages2 });
                          setDirty(true);
                        }}
                        className="text-xs px-2 py-1 hover:underline"
                        style={{ color: branding?.primaryColor || '#3b82f6' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = branding?.primaryColor || '#1e40af';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = branding?.primaryColor || '#3b82f6';
                        }}
                      >
                        + Add option
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(el.options || []).map((opt, optIdx) => (
                        <div key={optIdx} className="flex gap-2 items-center group">
                          <div className="flex-1 flex gap-2">
                            <input
                              className="flex-1 px-2 py-1.5 text-xs border rounded-lg focus:outline-2 focus:outline-primary"
                              placeholder="Option label"
                              value={typeof opt.label === 'object' ? (opt.label?.en || '') : (opt.label || '')}
                              onChange={e => {
                                const pages2 = [...form.pages];
                                const elements = [...pages2[activePageIdx].elements];
                                const idxEl = elements.findIndex(x => x.id === el.id);
                                const options = [...(elements[idxEl].options || [])];
                                options[optIdx] = {
                                  ...options[optIdx],
                                  value: options[optIdx].value || `option_${Date.now()}`,
                                  label: { ...(typeof options[optIdx].label === 'object' ? options[optIdx].label : {}), en: e.target.value }
                                };
                                elements[idxEl] = { ...elements[idxEl], options };
                                pages2[activePageIdx] = { ...pages2[activePageIdx], elements };
                                setForm({ ...form, pages: pages2 });
                                setDirty(true);
                              }}
                            />
                            <input
                              className="w-24 px-2 py-1.5 text-xs border rounded-lg focus:outline-2 focus:outline-primary"
                              placeholder="value"
                              value={opt.value || ''}
                              onChange={e => {
                                const pages2 = [...form.pages];
                                const elements = [...pages2[activePageIdx].elements];
                                const idxEl = elements.findIndex(x => x.id === el.id);
                                const options = [...(elements[idxEl].options || [])];
                                options[optIdx] = { ...options[optIdx], value: e.target.value };
                                elements[idxEl] = { ...elements[idxEl], options };
                                pages2[activePageIdx] = { ...pages2[activePageIdx], elements };
                                setForm({ ...form, pages: pages2 });
                                setDirty(true);
                              }}
                            />
                          </div>
                          <button
                            onClick={() => {
                              const pages2 = [...form.pages];
                              const elements = [...pages2[activePageIdx].elements];
                              const idxEl = elements.findIndex(x => x.id === el.id);
                              const options = [...(elements[idxEl].options || [])];
                              options.splice(optIdx, 1);
                              elements[idxEl] = { ...elements[idxEl], options };
                              pages2[activePageIdx] = { ...pages2[activePageIdx], elements };
                              setForm({ ...form, pages: pages2 });
                              setDirty(true);
                            }}
                            className="opacity-0 group-hover:opacity-60 hover:opacity-100 text-red-600 hover:text-red-700 transition-opacity"
                            title="Delete option"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                      {(!el.options || el.options.length === 0) && (
                        <div className="text-xs text-muted-foreground italic py-2 text-center border border-dashed rounded">
                          No options. Click "+ Add option" to create one.
                        </div>
                      )}
                    </div>
                    {(el.options || []).length > 0 && (
                      <div className="mt-2 text-[11px] text-slate-500">
                        {el.options.length} option{(el.options || []).length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                )}
                
                </>
                )}
                {propTab === 'validation' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-foreground mb-1">Regex (optional)</label>
                      <input className="w-full px-2 py-1.5 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring" value={el.validations?.regex || ''} onChange={e => {
                        const pages2 = [...form.pages];
                        const elements = [...pages2[activePageIdx].elements];
                        const idxEl = elements.findIndex(x => x.id === el.id);
                        const v = { ...(elements[idxEl].validations || {}), regex: e.target.value };
                        elements[idxEl] = { ...elements[idxEl], validations: v };
                        pages2[activePageIdx] = { ...pages2[activePageIdx], elements };
                      setForm({ ...form, pages: pages2 });
                      setDirty(true);
                      }} />
                      <div className="text-[11px] text-muted-foreground mt-1">Example: ^[0-9]{4}$</div>
                    </div>
                  </div>
                )}
                {propTab === 'logic' && (
                  <div className="space-y-3">
                    <div className="text-xs text-muted-foreground mb-2">
                      Show/hide this field based on other field values
                    </div>
                    {(() => {
                      // Get all fields for condition selection
                      const allFields = (form.pages || []).flatMap((p, pIdx) => 
                        (p.elements || [])
                          .filter(e => e.id !== el.id && e.type !== 'page_break' && e.type !== 'title' && e.type !== 'description')
                          .map(e => ({ ...e, pageIdx: pIdx, pageTitle: p.title || `Page ${pIdx + 1}` }))
                      );
                      
                      // Get existing logic rules that affect this field
                      const rulesForThisField = (form.logic || []).filter(r => 
                        r.scope === 'field' && r.targetId === el.id
                      );
                      
                      const addRule = () => {
                        if (allFields.length === 0) {
                          toast.error('Add other fields first to create conditions');
                          return;
                        }
                        const newRule = {
                          id: `rule_${Date.now()}`,
                          scope: 'field',
                          targetId: el.id,
                          action: 'show',
                          conditions: {
                            operator: 'and',
                            rules: [{
                              fieldKey: allFields[0]?.key || '',
                              operator: 'equals',
                              value: ''
                            }]
                          }
                        };
                        const logic = [...(form.logic || []), newRule];
                        setForm({ ...form, logic });
                        setDirty(true);
                      };
                      
                      return (
                        <>
                          {rulesForThisField.length === 0 ? (
                            <div className="text-xs text-muted-foreground py-4 text-center border border-dashed rounded">
                              No conditions set. This field will always be visible.
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {rulesForThisField.map((rule, ruleIdx) => (
                                <div key={rule.id || ruleIdx} className="p-3 border rounded-lg bg-muted">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-foreground">
                                      {rule.action === 'show' ? 'Show' : 'Hide'} when:
                                    </span>
                                    <button
                                      onClick={() => {
                                        const logic = (form.logic || []).filter(r => r.id !== rule.id);
                                        setForm({ ...form, logic });
                                        setDirty(true);
                                      }}
                                      className="text-red-600 hover:text-red-700 text-xs"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <div className="space-y-2">
                                    {Array.isArray(rule.conditions?.rules) ? (
                                      rule.conditions.rules.map((cond, condIdx) => (
                                        <div key={condIdx} className="flex gap-2 items-center">
                                          <select
                                            className="flex-1 text-xs px-2 py-1 border rounded"
                                            value={cond.fieldKey || ''}
                                            onChange={e => {
                                              const logic = [...(form.logic || [])];
                                              const ruleIdx = logic.findIndex(r => r.id === rule.id);
                                              if (ruleIdx >= 0) {
                                                logic[ruleIdx].conditions.rules[condIdx].fieldKey = e.target.value;
                                                setForm({ ...form, logic });
                                                setDirty(true);
                                              }
                                            }}
                                          >
                                            <option value="">Select field...</option>
                                            {allFields.map(f => (
                                              <option key={f.id} value={f.key}>
                                                {f.label?.en || f.key} ({f.pageTitle})
                                              </option>
                                            ))}
                                          </select>
                                          <select
                                            className="text-xs px-2 py-1 border rounded w-24"
                                            value={cond.operator || 'equals'}
                                            onChange={e => {
                                              const logic = [...(form.logic || [])];
                                              const ruleIdx = logic.findIndex(r => r.id === rule.id);
                                              if (ruleIdx >= 0) {
                                                logic[ruleIdx].conditions.rules[condIdx].operator = e.target.value;
                                                setForm({ ...form, logic });
                                                setDirty(true);
                                              }
                                            }}
                                          >
                                            <option value="equals">equals</option>
                                            <option value="not_equals">not equals</option>
                                            <option value="contains">contains</option>
                                            <option value="not_contains">not contains</option>
                                            <option value="greater_than">greater than</option>
                                            <option value="less_than">less than</option>
                                            <option value="is_empty">is empty</option>
                                            <option value="is_not_empty">is not empty</option>
                                          </select>
                                          {cond.operator !== 'is_empty' && cond.operator !== 'is_not_empty' && (
                                            <input
                                              className="flex-1 text-xs px-2 py-1 border rounded"
                                              placeholder="Value"
                                              value={cond.value || ''}
                                              onChange={e => {
                                                const logic = [...(form.logic || [])];
                                                const ruleIdx = logic.findIndex(r => r.id === rule.id);
                                                if (ruleIdx >= 0) {
                                                  logic[ruleIdx].conditions.rules[condIdx].value = e.target.value;
                                                  setForm({ ...form, logic });
                                                  setDirty(true);
                                                }
                                              }}
                                            />
                                          )}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="text-xs text-muted-foreground">Invalid condition format</div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <button
                            onClick={addRule}
                            className="w-full mt-3 px-3 py-2 text-xs border rounded-lg hover:bg-muted transition-colors"
                            disabled={allFields.length === 0}
                          >
                            + Add condition
                          </button>
                        </>
                      );
                    })()}
                  </div>
                )}
                {propTab === 'mapping' && (
                  <div className="space-y-3">
                    {(() => {
                      const current = (form.mapping || []).find(m => m.formFieldKey === el.key) || { formFieldKey: el.key, target: { entity: 'client', path: '' }, overwritePolicy: 'update_if_empty' };
                      const allKeys = (form.pages||[]).flatMap(p => (p.elements||[]).map(e => e.key));
                      const duplicateKey = allKeys.filter(k => k === el.key).length > 1;
                      const setKey = (newKey) => {
                        // normalize
                        const normalized = String(newKey || '').toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_{2,}/g,'_').replace(/^_+|_+$/g,'');
                        const pages2 = [...form.pages];
                        const elements = [...pages2[activePageIdx].elements];
                        const idxEl = elements.findIndex(x => x.id === el.id);
                        // update element key
                        elements[idxEl] = { ...elements[idxEl], key: normalized };
                        pages2[activePageIdx] = { ...pages2[activePageIdx], elements };
                        // update mapping reference
                        const mapping = [...(form.mapping || [])].filter(m => m.formFieldKey !== el.key);
                        mapping.push({ ...current, formFieldKey: normalized });
                        setForm({ ...form, pages: pages2, mapping });
                      };
                      return (
                        <>
                          <div>
                            <label className="block text-xs text-foreground mb-1">Field key</label>
                            <div className="flex gap-2">
                              <input className={`flex-1 px-2 py-1.5 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${duplicateKey ? 'border-red-400' : ''}`} value={el.key}
                                onChange={e => setKey(e.target.value)}
                                placeholder="e.g., full_name" />
                              <button
                                className="text-[11px] px-2 py-1.5 rounded-lg border bg-muted hover:bg-muted/80 whitespace-nowrap"
                                onClick={() => setKey(`${el.type}_${Math.random().toString(36).slice(2, 6)}`)}
                              >
                                Randomize
                              </button>
                            </div>
                            <div className={`text-[11px] mt-1 ${duplicateKey ? 'text-red-600' : 'text-slate-500'}`}>
                              {duplicateKey ? 'This key is duplicated. Make it unique.' : 'Machine key used for answers, mapping, and logic.'}
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-slate-700 mb-1">Entity</label>
                            <select className="w-full px-2 py-1.5 border rounded-lg focus:outline-2 focus:outline-primary" value={current.target.entity} onChange={e => {
                              const mapping = [...(form.mapping || [])].filter(m => m.formFieldKey !== el.key);
                              const keyRef = (form.pages||[]).flatMap(p => p.elements||[]).find(x => x.id === el.id)?.key || el.key;
                              mapping.push({ ...current, formFieldKey: keyRef, target: { ...current.target, entity: e.target.value } });
                              setForm({ ...form, mapping });
                              setDirty(true);
                            }}>
                              <option value="client">Client</option>
                              <option value="lead">Lead</option>
                              <option value="account">Account</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-foreground mb-1">CRM Path</label>
                            <input className="w-full px-2 py-1.5 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring" placeholder="e.g., contact.email or profile.cityPreference" value={current.target.path} onChange={e => {
                              const mapping = [...(form.mapping || [])].filter(m => m.formFieldKey !== el.key);
                              const keyRef = (form.pages||[]).flatMap(p => p.elements||[]).find(x => x.id === el.id)?.key || el.key;
                              mapping.push({ ...current, formFieldKey: keyRef, target: { ...current.target, path: e.target.value } });
                              setForm({ ...form, mapping });
                              setDirty(true);
                            }} />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-700 mb-1">Overwrite policy</label>
                            <select className="w-full px-2 py-1.5 border rounded-lg focus:outline-2 focus:outline-primary" value={current.overwritePolicy || 'update_if_empty'} onChange={e => {
                              const mapping = [...(form.mapping || [])].filter(m => m.formFieldKey !== el.key);
                              const keyRef = (form.pages||[]).flatMap(p => p.elements||[]).find(x => x.id === el.id)?.key || el.key;
                              mapping.push({ ...current, formFieldKey: keyRef, overwritePolicy: e.target.value });
                              setForm({ ...form, mapping });
                              setDirty(true);
                            }}>
                              <option value="update_if_empty">Update if empty</option>
                              <option value="always_overwrite">Always overwrite</option>
                              <option value="never_overwrite">Never overwrite</option>
                            </select>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })()}
            </div>
            {/* Sticky footer */}
            <div className="mt-auto p-3 border-t bg-card/50">
              <div className="flex justify-end">
                <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 rounded border hover:bg-muted text-sm">Save changes</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <FormShareModal isOpen={shareOpen} onClose={() => setShareOpen(false)} form={form} />
       
       {/* Preview Modal */}
       <Dialog isOpen={previewModalOpen} onClose={() => setPreviewModalOpen(false)} size="full">
         <DialogHeader 
           title="Form Preview" 
           onClose={() => setPreviewModalOpen(false)}
           className="border-b"
         />
         <DialogBody className="p-0">
          {/* Device Toggle in Modal */}
          <div className="sticky top-0 z-10 bg-card border-b px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-1 rounded-lg border border-border bg-muted p-0.5">
              <button 
                onClick={() => setPreviewDevice('desktop')} 
                className={cn(
                  "px-3 py-1.5 rounded text-sm font-medium transition-colors",
                  previewDevice === 'desktop' 
                    ? "bg-card text-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Desktop
              </button>
              <button 
                onClick={() => setPreviewDevice('mobile')} 
                className={cn(
                  "px-3 py-1.5 rounded text-sm font-medium transition-colors",
                  previewDevice === 'mobile' 
                    ? "bg-card text-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Mobile
              </button>
            </div>
            <div className="text-xs text-muted-foreground">
              Previewing as {previewDevice === 'mobile' ? 'mobile' : 'desktop'} device
            </div>
          </div>
          
          {/* Preview Content */}
          <div className="overflow-auto bg-muted p-6 scrollbar-thin" style={{ maxHeight: 'calc(100vh - 180px)' }}>
            {previewDevice === 'mobile' ? (
              <div className="mx-auto flex items-center justify-center">
                <PhoneMockup height={640}>
                  <FormPreview form={form} device={previewDevice} />
                </PhoneMockup>
              </div>
            ) : (
              <div className="mx-auto max-w-[860px]">
                <FormPreview form={form} device={previewDevice} />
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter className="border-t">
          <button
            onClick={() => setPreviewModalOpen(false)}
            className="px-4 py-2 rounded-lg border border-border hover:bg-muted text-sm font-medium"
          >
            Close Preview
          </button>
        </DialogFooter>
      </Dialog>

      {/* Invite Email Dialog */}
      <Dialog isOpen={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} size="md">
        <DialogHeader title="Send form invite" onClose={() => setInviteDialogOpen(false)} />
        <DialogBody>
          <p className="text-sm text-muted-foreground mb-3">Enter recipient emails (comma separated):</p>
          <input
            type="text"
            className="w-full px-3 py-2 border rounded-lg focus:outline-2 focus:outline-primary"
            placeholder="email1@example.com, email2@example.com"
            value={inviteEmailInput}
            onChange={(e) => setInviteEmailInput(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleInviteSubmit();
              }
            }}
          />
        </DialogBody>
        <DialogFooter>
          <button
            onClick={() => setInviteDialogOpen(false)}
            className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={handleInviteSubmit}
            className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity"
            style={{ backgroundColor: branding?.primaryColor || '#3b82f6' }}
          >
            Send
          </button>
        </DialogFooter>
      </Dialog>
      {cmdOpen && (
        <div className="fixed inset-0 z-[120] flex items-start justify-center pt-24 bg-black/50" onClick={() => setCmdOpen(false)}>
          <div className="bg-card w-full max-w-xl rounded-xl border shadow-lg p-3" onClick={e => e.stopPropagation()}>
            <input autoFocus placeholder="Type a command…" className="w-full px-3 py-2 border rounded-lg mb-2 bg-background" onKeyDown={(e) => {
              if (e.key === 'Escape') setCmdOpen(false);
            }} />
            <div className="text-sm">
              <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted" onClick={() => { addBlock('short_text'); setCmdOpen(false); }}>Add short text</button>
              <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted" onClick={() => { addBlock('long_text'); setCmdOpen(false); }}>Add long text</button>
              <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted" onClick={() => { addBlock('email'); setCmdOpen(false); }}>Add email</button>
              <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted" onClick={() => { addPage(); setCmdOpen(false); }}>Add page</button>
              <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted" onClick={() => { handleInvite(); setCmdOpen(false); }}>Invite</button>
              <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted" onClick={() => { setShareOpen(true); setCmdOpen(false); }}>Share</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Small UI helpers
const BlockCard = ({ label, description, icon, tier, onClick }) => (
  <button onClick={onClick} className="w-full text-left px-3 py-2 rounded-2xl border bg-background hover:bg-muted transition shadow-sm">
    <div className="flex items-center gap-2">
      <div className="text-sm font-medium flex-1 text-slate-800">{label}</div>
      {tier && tier !== 'Free' && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-1">
          <span>🔒</span>
          <span>{tier}</span>
        </span>
      )}
    </div>
    {description && <div className="text-[11px] text-slate-600 mt-0.5">{description}</div>}
    <div className="text-[11px] text-slate-500 mt-0.5">Drag or click to add</div>
  </button>
);

const TemplateButton = ({ label, onClick }) => (
  <button onClick={onClick} className="w-full text-left px-3 py-2 rounded-lg border hover:bg-muted text-sm">
    {label}
  </button>
);

const QuickChip = ({ children, className, ...props }) => (
  <button 
    {...props}
    className={cn("text-[11px] px-2 py-0.5 rounded-full border bg-card shadow-sm hover:bg-muted transition", className)}
  >
    {children}
  </button>
);

export default FormBuilder;


